/**
 * Player Intelligence — Análisis completo de un partido (Fase 2).
 *
 * ARQUITECTURA
 *   RAW PROVIDER DATA → normalizePlayerMatch() → PLAYER MATCH → UI
 *
 * REGLA INVIOLABLE
 *   Esta página CONSUME EXCLUSIVAMENTE el objeto normalizado.
 *   NO se lee `source_raw` bajo ninguna circunstancia.
 *
 * Ruta:  /jugadores/:playerId/analisis/:matchId
 * Prop:  ninguna. Se leen `playerId` y `matchId` desde useParams().
 * Datos maestros: se obtienen de la API pública actual utilizando los
 * slugs proporcionados por el fixture (`getPlayerIntelligenceContextById`).
 * Cuando el backend Player Intelligence real esté operativo, el mismo
 * contrato normalizado alimentará esta pantalla sin cambios en la UI.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, BarChart3, Activity, Target, Send, Swords, Shield,
  Move3D, Users, ListChecks, GitBranch, CalendarClock, Timer, MapPin,
  Trophy, ShieldQuestion, CheckCircle2, Lightbulb, Crown, Lock,
  AlertCircle,
} from 'lucide-react';
import { fetchPlayerIntelligenceMatch, friendlyError, buildAssetUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { HeatmapImage } from '@/components/players/HeatmapImage';
import {
  selectKpisForPlayerMatch,
  formatMatchDateEs,
  formatVideoTimestamp,
} from '@/lib/playerIntelligence';
import { ROUTES } from '@/lib/constants';

/* -------------------------------------------------------------------------- */
/* Helpers de presentación                                                    */
/* -------------------------------------------------------------------------- */

/** Formatea un número o devuelve `null` si viene null/undefined. `0` es válido. */
const fmtNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n)) : null;
};
const fmtDec = (v, digits = 1) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : null;
};
const fmtPct = (v) => {
  const s = fmtDec(v, 1);
  return s === null ? null : `${s}%`;
};

const QUALITY_META = {
  OK: { label: 'OK', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  PARTIAL: { label: 'Parcial', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  REVIEW: { label: 'Revisión', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
};

function isPartial(status) {
  return status !== 'complete';
}

/* -------------------------------------------------------------------------- */
/* Página                                                                     */
/* -------------------------------------------------------------------------- */

export default function PlayerMatchAnalysis() {
  const { playerId, matchId } = useParams();

  // Fase 3A — La página consume EXCLUSIVAMENTE la API real:
  //   GET /player-intelligence/players/{player_profile_id}/matches/{player_match_id}
  // El servidor aplica la autorización server-side (PÚBLICO | PROPIETARIO |
  // ADMIN) antes de devolver el payload y filtra `raw_json`/`source_raw`.
  // Sin fallback runtime a fixtures/registry.
  const [detail, setDetail] = useState(null);
  const [masterLoading, setMasterLoading] = useState(true);
  const [masterError, setMasterError] = useState(null);
  // Se conservan los flags de rol como capa adicional en la UI, pero la
  // decisión real proviene del backend: si la API responde 200, hay
  // autorización efectiva; si responde 404, no hay autorización.
  const { isAdmin, isSuperAdmin } = useAuth();
  const adminAuthorized = !!(isAdmin || isSuperAdmin);

  useEffect(() => {
    let cancelled = false;
    setMasterLoading(true);
    setMasterError(null);
    setDetail(null);
    (async () => {
      try {
        const data = await fetchPlayerIntelligenceMatch(playerId, matchId);
        if (cancelled) return;
        setDetail(data || null);
      } catch (err) {
        if (cancelled) return;
        setMasterError(friendlyError(err) || 'public_profile_not_available');
      } finally {
        if (!cancelled) setMasterLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId, matchId]);

  const playerMatch = detail?.normalized_data || null;
  const master = useMemo(() => {
    const card = detail?.player_card;
    if (!card) return null;
    // Adaptador mínimo al shape que ya esperan `AnalysisHeader` y `BackLink`:
    // provee `slug`, `first_name`, `last_name`, `position`, `profile_photo`,
    // `club_name`, `team_name`, `gender`. `club_slug`/`team_slug` se usan
    // sólo para la navegación de vuelta.
    return {
      slug: card.slug || null,
      first_name: card.first_name || null,
      last_name: card.last_name || null,
      gender: card.gender || null,
      position: card.position || null,
      profile_photo: card.profile_photo || null,
      club_name: card.club_name || null,
      team_name: card.team_name || null,
      _access: adminAuthorized ? 'admin' : undefined,
    };
  }, [detail, adminAuthorized]);

  const slugs = useMemo(() => {
    const card = detail?.player_card;
    if (!card) return null;
    if (!card.club_slug || !card.team_slug || !card.slug) return null;
    return {
      club_slug: card.club_slug,
      team_slug: card.team_slug,
      player_slug: card.slug,
    };
  }, [detail]);

  const backHref = slugs
    ? `/clubes/${encodeURIComponent(slugs.club_slug)}/equipos/${encodeURIComponent(slugs.team_slug)}`
    : ROUTES.home;

  // Loading: no renderizamos ningún dato del análisis hasta confirmar autorización.
  if (masterLoading) {
    return (
      <MainLayout>
        <div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          data-testid="player-analysis-loading"
        >
          <Skeleton className="h-6 w-40 mb-6" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </MainLayout>
    );
  }

  // FAIL-CLOSED: sin autorización pública (equivalente a la ficha oculta),
  // no montamos ningún dato de Player Intelligence.
  if (masterError || !master || !playerMatch) {
    return (
      <MainLayout>
        <div
          data-testid="player-analysis-unauthorized"
          className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center"
        >
          <ShieldQuestion className="h-12 w-12 text-[var(--ns-text-muted)] mx-auto mb-4" />
          <h1 className="font-display text-2xl text-[var(--ns-text)] mb-2">
            Este perfil no está disponible para visualización pública.
          </h1>
          <p className="text-sm text-[var(--ns-text-muted)] mb-6">
            Puede que el club o el administrador no haya autorizado su consulta pública.
          </p>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to={backHref} data-testid="player-analysis-unauthorized-back">
              ← Volver a la plantilla
            </Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
        data-testid="player-analysis-root"
      >
        <BackLink master={master} slugs={slugs} />
        <AnalysisHeader
          master={master}
          masterLoading={false}
          masterError={null}
          playerMatch={playerMatch}
        />

        {/* Fila de 4 KPIs grandes por posición (ROLE_CONFIG). */}
        <KpiCard playerMatch={playerMatch} />

        {hasMeaningfulIndividualData(playerMatch) ? (
          <>
            {/* Bloque principal — Mapa + Posicionamiento en 2 columnas. */}
            <MapAndPositioningRow positioning={playerMatch.positioning} />

            {/* Grid dinámico de rendimiento — sólo se renderiza lo que tiene datos. */}
            <PerformanceGrid playerMatch={playerMatch} />
          </>
        ) : (
          <InsufficientIndividualDataNotice />
        )}

        {/*
          Simplificación UI (microfase): Sección "Acciones del partido"
          (KeyActionsSection) oculta del render. Incluye:
            - listado de acciones en tiempo reglamentario;
            - listado de acciones de la tanda de penaltis;
            - timestamps visibles ("Vídeo mm:ss");
            - cualquier CTA/enlace a vídeo asociado;
            - `PenaltyShootoutCard` (bundle con las acciones).
          Los datos internos (`playerMatch.key_actions`, `penalty_shootout`,
          `event_details`, `timestamps`, `video_url`) permanecen intactos en
          el contrato y en el normalizador; sólo dejan de renderizarse.
        */}
        {/* <KeyActionsSection playerMatch={playerMatch} /> */}

        {/* Producto Premium — sólo UI bloqueada. */}
        <IntelligencePlusBlock />
      </div>
    </MainLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout: Mapa + Posicionamiento                                             */
/* -------------------------------------------------------------------------- */

/**
 * Muestra Mapa de actividad + Posicionamiento en un grid de 2 columnas
 * (en desktop) que se apila en móvil. Reorganiza el espacio si alguno
 * de los dos bloques no tiene datos.
 *
 * Regla (spec 7): si `positions_recorded === 0` o no hay heatmap ni
 * positions_xy fiables → Mapa completamente oculto. Si además tampoco
 * hay tercios ni distribución lateral → Posicionamiento también oculto.
 * En ese caso el bloque entero desaparece.
 */
function MapAndPositioningRow({ positioning }) {
  const hasXy = Array.isArray(positioning?.positions_xy) && positioning.positions_xy.length > 0;
  const url = positioning?.heatmap_url || null;
  const positionsRecorded = typeof positioning?.positions_recorded === 'number'
    ? positioning.positions_recorded : null;
  const showMap = (hasXy || !!url) && (positionsRecorded === null || positionsRecorded > 0);

  const thirds = positioning?.thirds_pct || null;
  const sides = positioning?.sides_pct || null;
  const zones = Array.isArray(positioning?.most_active_zones) ? positioning.most_active_zones : [];
  const hasThirds = thirds && (thirds.defensive !== null || thirds.middle !== null || thirds.attacking !== null);
  const hasSides = sides && (sides.left !== null || sides.centre !== null || sides.right !== null);
  const showPositioning = hasThirds || hasSides || zones.length > 0;

  if (!showMap && !showPositioning) return null;

  return (
    <div
      className={`mt-4 grid gap-4 ${
        showMap && showPositioning ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
      }`}
      data-testid="player-analysis-map-positioning-row"
    >
      {showMap ? <ActivityMapCard positioning={positioning} /> : null}
      {showPositioning ? <PositioningCard positioning={positioning} /> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Grid dinámico de rendimiento                                               */
/* -------------------------------------------------------------------------- */

/**
 * Determina si un análisis contiene evidencia individual utilizable del
 * jugador. NO cuenta datos administrativos del partido (fecha, rival,
 * marcador…), NO cuenta `player.minutes_played`, NO cuenta métricas de
 * equipo (`team_context.*`, `sequences.team_total_sequences`) y NO cuenta
 * las tarjetas Intelligence+ bloqueadas.
 *
 * Se considera evidencia individual cualquiera de:
 *   passing.attempted / completed / accuracy_pct / avg_length_m /
 *     avg_completed_length_m / avg_failed_length_m / events.length > 0
 *   receiving.total / defensive_third / middle_third / attacking_third /
 *     inside_box / under_pressure / events.length > 0
 *   defending.recoveries / clearances / blocks / to_opponent /
 *     tackles.attempted / tackles.won / pressures / high_press_actions /
 *     fouls_committed / events.length > 0
 *   duels.total_events / resolved / won / lost / win_pct
 *   finishing_regulation.shots / goals / on_target / off_target /
 *     blocked / inside_box / outside_box / chances_created / conversion_pct
 *   crossing.attempted / completed / events.length > 0
 *   positioning.positions_xy (>0) / positions_recorded (>0) /
 *     thirds_pct / sides_pct / most_active_zones.length > 0
 *   possession_progression.ball_losses / progressive_runs /
 *     final_third_entries
 *   sequences.passes_given / passes_received / longest / longest_sequence
 *
 * Cambio local a este componente: NO altera el contrato ni el backend.
 */
function hasMeaningfulIndividualData(playerMatch) {
  if (!playerMatch || typeof playerMatch !== 'object') return false;
  const isNum = (v) => v !== null && v !== undefined && !Number.isNaN(v);
  const hasArr = (v) => Array.isArray(v) && v.length > 0;
  const anyNum = (obj, keys) => obj && keys.some((k) => isNum(obj[k]));

  const passing = playerMatch.passing || {};
  if (anyNum(passing, ['attempted', 'completed', 'accuracy_pct', 'avg_length_m', 'avg_completed_length_m', 'avg_failed_length_m'])) return true;
  if (hasArr(passing.events)) return true;

  const receiving = playerMatch.receiving || {};
  if (anyNum(receiving, ['total', 'defensive_third', 'middle_third', 'attacking_third', 'inside_box', 'under_pressure'])) return true;
  if (hasArr(receiving.events)) return true;

  const defending = playerMatch.defending || {};
  if (anyNum(defending, ['recoveries', 'clearances', 'blocks', 'to_opponent', 'pressures', 'high_press_actions', 'fouls_committed'])) return true;
  const tackles = defending.tackles || {};
  if (anyNum(tackles, ['attempted', 'won'])) return true;
  if (hasArr(defending.events)) return true;

  const duels = playerMatch.duels || {};
  if (anyNum(duels, ['total_events', 'resolved', 'won', 'lost', 'win_pct'])) return true;

  const finishing = playerMatch.finishing_regulation || {};
  if (anyNum(finishing, ['shots', 'goals', 'on_target', 'off_target', 'blocked', 'inside_box', 'outside_box', 'chances_created', 'conversion_pct'])) return true;

  const crossing = playerMatch.crossing || {};
  if (anyNum(crossing, ['attempted', 'completed'])) return true;
  if (hasArr(crossing.events)) return true;

  const positioning = playerMatch.positioning || {};
  if (hasArr(positioning.positions_xy)) return true;
  if (isNum(positioning.positions_recorded) && positioning.positions_recorded > 0) return true;
  if (positioning.thirds_pct && typeof positioning.thirds_pct === 'object'
      && Object.values(positioning.thirds_pct).some(isNum)) return true;
  if (positioning.sides_pct && typeof positioning.sides_pct === 'object'
      && Object.values(positioning.sides_pct).some(isNum)) return true;
  if (hasArr(positioning.most_active_zones)) return true;

  const possession = playerMatch.possession_progression || {};
  if (anyNum(possession, ['ball_losses', 'progressive_runs', 'final_third_entries'])) return true;

  const sequences = playerMatch.sequences || {};
  // Sólo métricas INDIVIDUALES de secuencias — `team_total_sequences` NO cuenta.
  if (anyNum(sequences, ['passes_given', 'passes_received', 'longest', 'longest_sequence'])) return true;

  return false;
}

/**
 * Aviso que se muestra cuando el análisis persiste pero no contiene
 * evidencia individual utilizable. Sustituye a la parrilla y al bloque de
 * mapa/posicionamiento, evitando el efecto "tarjeta única" cuando el RAW
 * sólo trae datos de equipo (p. ej. `sequences.team_total_sequences`).
 */
function InsufficientIndividualDataNotice() {
  return (
    <Card
      data-testid="player-analysis-insufficient-notice"
      className="mt-4 p-6 sm:p-8 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)] text-center"
    >
      <div className="mx-auto h-12 w-12 rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
        <AlertCircle className="h-6 w-6" />
      </div>
      <h2 className="mt-4 font-display text-lg sm:text-xl text-[var(--ns-text)]">
        Datos insuficientes para generar Player Intelligence de este partido.
      </h2>
      <p className="mt-2 text-sm text-[var(--ns-text-muted)] max-w-xl mx-auto">
        La información disponible no permite generar estadísticas individuales
        fiables para este jugador.
      </p>
    </Card>
  );
}

/**
 * Renderiza únicamente las tarjetas cuyos bloques tienen datos utilizables.
 * El grid se reorganiza automáticamente (1/2/3/4+ columnas) según el número
 * de tarjetas visibles: sin huecos ni tarjetas vacías.
 */
function PerformanceGrid({ playerMatch }) {
  const passing = playerMatch.passing || {};
  const defending = playerMatch.defending || {};
  const finishing = playerMatch.finishing_regulation || {};
  const duels = playerMatch.duels || {};
  const crossing = playerMatch.crossing || {};
  const sequences = playerMatch.sequences || {};
  const team = playerMatch.team_context || {};

  const blocks = [];
  // Pase: se muestra si hay al menos attempted/completed confirmados o events.
  const passingHasData = passing.attempted !== null
    || passing.completed !== null
    || passing.accuracy_pct !== null
    || (Array.isArray(passing.events) && passing.events.length > 0);
  if (passingHasData) {
    blocks.push(
      <PassingCard
        key="passing"
        passing={passing}
        eventsCompleteness={playerMatch.data_quality?.block_completeness?.passing_events}
      />,
    );
  }
  // Defensa: cualquier métrica confirmada.
  const defHasData = ['recoveries','clearances','blocks','to_opponent','tackles_attempted','tackles_won','fouls_committed','pressures','high_press_actions']
    .some((k) => defending[k] !== null && defending[k] !== undefined);
  if (defHasData) blocks.push(<DefendingCard key="defending" defending={defending} />);
  // Duelos: sólo si won o lost están confirmados (regla existente en duels card).
  if (duels.won !== null || duels.lost !== null || duels.total_events !== null) {
    blocks.push(<DuelsCard key="duels" duels={duels} />);
  }
  // Finalización: sólo si hay tiros/goles/dentro-fuera confirmados.
  const finHasData = ['shots','goals','on_target','off_target','blocked','inside_box','outside_box','chances_created']
    .some((k) => finishing[k] !== null && finishing[k] !== undefined);
  if (finHasData) blocks.push(<FinishingCard key="finishing" finishing={finishing} />);
  // Centros.
  const crossHasData = ['attempted','completed'].some((k) => crossing[k] !== null && crossing[k] !== undefined);
  if (crossHasData) blocks.push(<CrossingCard key="crossing" crossing={crossing} />);
  // Secuencias.
  const seqHasData = ['passes_given','passes_received','longest','team_total_sequences']
    .some((k) => sequences[k] !== null && sequences[k] !== undefined);
  if (seqHasData) {
    blocks.push(
      <SequencesCard
        key="sequences"
        sequences={sequences}
        status={playerMatch.data_quality?.block_completeness?.sequences}
      />,
    );
  }
  // Impacto en el equipo (shares únicamente).
  const shareValues = Object.values(team.player_share_pct || {}).filter((v) => v !== null && v !== undefined);
  if (shareValues.length > 0) blocks.push(<TeamImpactCard key="impact" teamContext={team} />);

  if (blocks.length === 0) return null;
  return (
    <div
      className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-min"
      data-testid="player-analysis-performance-grid"
    >
      {blocks}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sección — Acciones del partido                                             */
/* -------------------------------------------------------------------------- */

function KeyActionsSection({ playerMatch }) {
  const actions = Array.isArray(playerMatch.key_actions) ? playerMatch.key_actions : [];
  const shootout = playerMatch.penalty_shootout;
  const showShootout = shootout && (shootout.shots > 0 || (Array.isArray(shootout.events) && shootout.events.length > 0));
  if (actions.length === 0 && !showShootout) return null;
  return (
    <div
      className={`mt-4 grid gap-4 ${showShootout ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}
      data-testid="player-analysis-actions-row"
    >
      <div className={showShootout ? 'lg:col-span-2' : ''}>
        {actions.length > 0 ? <KeyActionsCard actions={actions} /> : null}
      </div>
      {showShootout ? <PenaltyShootoutCard shootout={shootout} /> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bloque Intelligence+ (UI premium bloqueada — sin lógica interpretativa)    */
/* -------------------------------------------------------------------------- */

/**
 * Sección visual que representa el producto Player Intelligence+. Esta fase
 * NO contiene interpretación automática ni IA: sólo cuatro tarjetas
 * bloqueadas + un CTA visual. Cualquier flujo comercial se implementará
 * en una fase posterior.
 */
function IntelligencePlusBlock() {
  const cards = [
    { key: 'strengths',       title: 'Fortalezas observadas',        Icon: CheckCircle2 },
    { key: 'patterns',        title: 'Patrones a observar',          Icon: BarChart3   },
    { key: 'focus',           title: 'Seguimiento del foco',         Icon: Target      },
    { key: 'recommendations', title: 'Recomendaciones personalizadas', Icon: Lightbulb },
  ];
  return (
    <section
      className="mt-6 rounded-2xl overflow-hidden border border-[var(--ns-border)] bg-gradient-to-br from-[var(--ns-gold)]/12 via-white to-[var(--ns-gold)]/8"
      data-testid="player-analysis-intelligence-plus"
    >
      <div className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-[var(--ns-border)]/60 bg-white/50">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-4 w-4 text-[var(--ns-gold-700)]" />
            <p className="font-display text-lg text-[var(--ns-text)]">Intelligence+</p>
          </div>
          <p className="text-sm text-[var(--ns-text-muted)] leading-snug">
            Desbloquea el análisis avanzado con interpretaciones, patrones y
            recomendaciones personalizadas para este partido.
          </p>
        </div>
        <Button
          type="button"
          disabled
          className="rounded-xl bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-600)] text-black h-10 px-4 text-sm font-medium shrink-0 shadow-none"
          data-testid="player-analysis-intelligence-plus-cta"
        >
          <Lock className="h-4 w-4 mr-2" />
          Desbloquear informe completo
        </Button>
      </div>
      <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map(({ key, title, Icon }) => (
          <div
            key={key}
            className="rounded-2xl bg-white border border-[var(--ns-border)] p-4 flex flex-col gap-3"
            data-testid={`player-analysis-intelligence-plus-card-${key}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                <p className="text-sm font-semibold text-[var(--ns-text)] truncate">
                  {title}
                </p>
              </div>
              <Lock className="h-3.5 w-3.5 text-[var(--ns-text-subtle)]" aria-hidden="true" />
            </div>
            {/* Placeholder visual — sin contenido interpretativo. */}
            <div className="space-y-1.5">
              <div className="h-2 w-full rounded-full bg-[var(--ns-bg-2)]" />
              <div className="h-2 w-5/6 rounded-full bg-[var(--ns-bg-2)]" />
              <div className="h-2 w-2/3 rounded-full bg-[var(--ns-bg-2)]" />
            </div>
            <p className="text-[11px] text-[var(--ns-text-subtle)] mt-auto">
              Disponible en tu Player Intelligence Report.
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Encabezado                                                                 */
/* -------------------------------------------------------------------------- */

function BackLink({ master, slugs }) {
  if (!slugs) return null;
  const href = `/clubes/${encodeURIComponent(slugs.club_slug)}/equipos/${encodeURIComponent(slugs.team_slug)}/jugadores/${encodeURIComponent(slugs.player_slug)}`;
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1.5 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-text)] transition"
      data-testid="player-analysis-back"
    >
      <ArrowLeft className="h-4 w-4" />
      {master?.full_name ? `Volver a ${master.full_name}` : 'Volver a la ficha'}
    </Link>
  );
}

function AnalysisHeader({ master, masterLoading, masterError, playerMatch }) {
  const { match, player } = playerMatch;
  const quality = playerMatch.data_quality?.status || 'REVIEW';
  const qualityMeta = QUALITY_META[quality] || QUALITY_META.REVIEW;
  const dateLabel = formatMatchDateEs(match.date);
  const avatarSrc = master ? (buildAssetUrl(master.photo_url) || resolvePlayerAvatar(master)) : null;

  return (
    <Card
      data-testid="player-analysis-header"
      className="p-4 sm:p-5 rounded-2xl mt-4 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Identidad del jugador */}
        <div className="flex items-center gap-3 min-w-0 md:min-w-[240px]">
          {masterLoading ? (
            <Skeleton className="h-16 w-16 rounded-2xl" />
          ) : avatarSrc ? (
            <img
              src={avatarSrc}
              alt={master?.full_name || 'Jugador'}
              className="h-16 w-16 rounded-2xl object-cover border border-[var(--ns-border)]"
              draggable={false}
              data-testid="player-analysis-avatar"
            />
          ) : (
            <div className="h-16 w-16 rounded-2xl bg-[var(--ns-bg-2)] border border-[var(--ns-border)]" />
          )}
          <div className="min-w-0">
            <h1
              className="font-display text-lg sm:text-xl text-[var(--ns-text)] leading-tight truncate"
              data-testid="player-analysis-player-name"
            >
              {masterLoading ? (
                <Skeleton className="h-5 w-44" />
              ) : masterError ? (
                'Jugador'
              ) : (
                master?.full_name || 'Jugador'
              )}
            </h1>
            {master?.team_name || master?.club_name ? (
              <p className="text-xs text-[var(--ns-text-muted)] truncate">
                {[master.club_name, master.team_name].filter(Boolean).join(' · ')}
              </p>
            ) : null}
          </div>
        </div>

        {/* Metadatos inline compactos */}
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <MetaItem icon={CalendarClock} label="Fecha" value={dateLabel} testId="player-analysis-date" />
          <MetaItem icon={Users} label="Rival" value={match.opponent} testId="player-analysis-opponent" />
          <MetaItem icon={MapPin} label="Posición" value={player.position} testId="player-analysis-position" />
          <MetaItem
            icon={Timer}
            label="Minutos"
            value={player.minutes_played !== null ? `${player.minutes_played}′` : null}
            testId="player-analysis-minutes"
          />
        </div>

        {/* Marcador + calidad */}
        <div className="flex flex-row md:flex-col items-center md:items-end gap-2 md:gap-1.5 shrink-0">
          <Badge
            data-testid="player-analysis-quality-badge"
            className={`h-6 rounded-full text-[10px] uppercase tracking-wider border ${qualityMeta.cls}`}
          >
            {qualityMeta.label}
          </Badge>
          {match.score_regulation ? (
            <p
              className="text-xl sm:text-2xl font-display leading-none"
              data-testid="player-analysis-score"
            >
              {match.score_regulation}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function MetaItem({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-[var(--ns-gold-700)] shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] font-semibold">
          {label}
        </p>
        <p
          className="text-sm text-[var(--ns-text)] truncate"
          data-testid={testId}
        >
          {value ?? '—'}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Building blocks reutilizables                                              */
/* -------------------------------------------------------------------------- */

function SectionCard({ icon: Icon, title, note, children, testId }) {
  return (
    <Card
      data-testid={testId}
      className="p-4 sm:p-5 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {Icon ? <Icon className="h-4 w-4 text-[var(--ns-gold-600)] shrink-0" /> : null}
          <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold leading-tight">
            {title}
          </h2>
        </div>
        {note ? (
          <span className="text-[9px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0">
            {note}
          </span>
        ) : null}
      </div>
      {children}
    </Card>
  );
}

/**
 * `Metric` renderiza {label, value}. Si `value === null` NO se renderiza
 * (regla null≠0). `hint` es un texto factual discreto opcional.
 */
function Metric({ label, value, hint, testId }) {
  if (value === null || value === undefined) return null;
  return (
    <div
      className="p-2.5 rounded-xl border border-[var(--ns-border)] bg-[var(--ns-amber-50)]/30 flex flex-col min-w-0"
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-wider text-[var(--ns-text-muted)] font-semibold leading-tight break-words">
        {label}
      </p>
      <p className="text-lg sm:text-xl font-display text-[var(--ns-text)] mt-1 leading-none tabular-nums">
        {value}
      </p>
      {hint ? (
        <p className="text-[10px] text-[var(--ns-text-subtle)] mt-1.5 break-words">{hint}</p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI Row                                                                    */
/* -------------------------------------------------------------------------- */

function KpiCard({ playerMatch }) {
  const kpis = useMemo(
    () => selectKpisForPlayerMatch(playerMatch).filter((k) => k.formatted !== null),
    [playerMatch],
  );
  if (kpis.length === 0) return null;
  // Iconos indicativos por familia — no cambia semántica.
  const iconFor = (key) => {
    if (key === 'passing_accuracy') return GitBranch;
    if (key === 'progressive_completed') return Send;
    if (key === 'recoveries') return Activity;
    if (key === 'clearances') return Shield;
    if (key === 'goals' || key === 'shots') return Target;
    return Trophy;
  };
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4"
      data-testid="player-analysis-kpis"
    >
      {kpis.map((k) => {
        const Icon = iconFor(k.key);
        return (
          <Card
            key={k.key}
            className="p-4 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="h-9 w-9 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] font-semibold leading-tight">
                  {k.label}
                </p>
                <p
                  className="font-display text-2xl sm:text-3xl leading-none mt-1 text-[var(--ns-text)] tabular-nums"
                  data-testid={`player-analysis-kpi-${k.key}`}
                >
                  {k.formatted}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Activity Map                                                               */
/* -------------------------------------------------------------------------- */

function ActivityMapCard({ positioning }) {
  const hasXy = Array.isArray(positioning?.positions_xy) && positioning.positions_xy.length > 0;
  const url = positioning?.heatmap_url || null;
  if (!hasXy && !url) {
    return (
      <SectionCard icon={Move3D} title="Mapa de actividad" testId="player-analysis-activity-map">
        <p className="text-xs text-[var(--ns-text-subtle)]">Sin datos de posicionamiento disponibles.</p>
      </SectionCard>
    );
  }
  const coverageNote =
    positioning?.coverage === 'partial'
      ? 'Cobertura parcial'
      : positioning?.coverage === 'unknown'
        ? 'Cobertura desconocida'
        : null;
  const positionsRecorded =
    typeof positioning?.positions_recorded === 'number' && positioning.positions_recorded > 0
      ? positioning.positions_recorded
      : null;
  return (
    <SectionCard
      icon={Move3D}
      title="Mapa de actividad"
      note={coverageNote}
      testId="player-analysis-activity-map"
    >
      {hasXy ? (
        <p className="text-xs text-[var(--ns-text-subtle)]">
          NetScouters generará el heatmap desde las coordenadas XY cuando la cobertura sea completa.
        </p>
      ) : (
        <HeatmapImage
          url={url}
          className="rounded-xl overflow-hidden border border-[var(--ns-border)] bg-[var(--ns-bg-2)]/40 p-2"
          maxHeightPx={320}
          testId="player-analysis-activity-map-image"
        />
      )}
      {positionsRecorded !== null ? (
        <p
          className="text-[10px] text-[var(--ns-text-subtle)] mt-2 text-center"
          data-testid="player-analysis-activity-map-caption"
        >
          {positionsRecorded} posiciones registradas
        </p>
      ) : null}
      {positioning?.coverage === 'partial' ? (
        <p className="text-[10px] text-[var(--ns-text-subtle)] mt-1">
          Cobertura de posicionamiento: parcial.
        </p>
      ) : null}
    </SectionCard>
  );
}

/**
 * HeatmapImage — implementación en `@/components/players/HeatmapImage`.
 * Elimina la etiqueta `<text>` con "events" del SVG del proveedor y añade
 * un caption factual desde el contenedor.
 */

/* -------------------------------------------------------------------------- */
/* Positioning (thirds + sides + zones)                                       */
/* -------------------------------------------------------------------------- */

function PositioningCard({ positioning }) {
  const thirds = positioning?.thirds_pct || null;
  const sides = positioning?.sides_pct || null;
  const zones = Array.isArray(positioning?.most_active_zones) ? positioning.most_active_zones : null;
  if (!thirds && !sides && (!zones || zones.length === 0)) return null;
  return (
    <SectionCard icon={MapPin} title="Posicionamiento" testId="player-analysis-positioning">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {thirds ? (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[var(--ns-text-muted)] mb-2 font-semibold">
              Distribución por tercios
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <ZonePill label="Defensivo" pct={thirds.defensive} testId="pos-third-def" />
              <ZonePill label="Medio" pct={thirds.middle} testId="pos-third-mid" />
              <ZonePill label="Ofensivo" pct={thirds.attacking} testId="pos-third-att" highlight />
            </div>
          </div>
        ) : null}
        {sides ? (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[var(--ns-text-muted)] mb-2 font-semibold">
              Distribución lateral
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <ZonePill label="Izquierda" pct={sides.left} testId="pos-side-left" highlight />
              <ZonePill label="Centro" pct={sides.centre ?? sides.center} testId="pos-side-center" />
              <ZonePill label="Derecha" pct={sides.right} testId="pos-side-right" />
            </div>
          </div>
        ) : null}
      </div>
      {zones && zones.length > 0 ? (
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider text-[var(--ns-text-muted)] mb-2 font-semibold">
            Zonas más activas
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="pos-zones">
            {zones.slice(0, 6).map((z, i) => (
              <span
                key={`${z.zone}-${i}`}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-[var(--ns-border)] bg-[var(--ns-bg-2)]/60 text-[var(--ns-text)]"
              >
                <span className="text-[var(--ns-text-muted)]">{z.zone}</span>
                <strong>{fmtPct(z.pct) ?? '—'}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

function ZonePill({ label, pct, testId, highlight }) {
  const s = fmtPct(pct);
  if (s === null) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--ns-border)] py-3 text-[var(--ns-text-subtle)] text-xs">
        {label}
        <br /><span className="text-[10px]">—</span>
      </div>
    );
  }
  return (
    <div
      className={`rounded-xl border py-3 ${highlight ? 'border-[var(--ns-gold-500)] bg-[var(--ns-amber-50)]' : 'border-[var(--ns-border)] bg-[var(--ns-bg-2)]/30'}`}
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-wider text-[var(--ns-text-muted)] font-semibold">
        {label}
      </p>
      <p className="text-base font-display text-[var(--ns-text)] mt-1 leading-none">{s}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Finishing                                                                  */
/* -------------------------------------------------------------------------- */

function FinishingCard({ finishing }) {
  if (!finishing) return null;
  const items = [
    { key: 'shots', label: 'Tiros', value: fmtNum(finishing.shots) },
    { key: 'goals', label: 'Goles', value: fmtNum(finishing.goals) },
    { key: 'blocked', label: 'Bloqueados', value: fmtNum(finishing.blocked) },
    { key: 'off_target', label: 'Fuera', value: fmtNum(finishing.off_target) },
    {
      key: 'on_target',
      label: 'A puerta',
      value: fmtNum(finishing.on_target),
      hint:
        finishing.on_target === 0 && (finishing.goals || 0) > 0
          ? 'La fuente registra "Goal" y "On Target" como categorías independientes.'
          : null,
    },
    { key: 'inside_box', label: 'Dentro del área', value: fmtNum(finishing.inside_box) },
    { key: 'outside_box', label: 'Fuera del área', value: fmtNum(finishing.outside_box) },
    { key: 'conversion_pct', label: 'Conversión', value: finishing.conversion_pct !== null ? `${finishing.conversion_pct}%` : null },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  return (
    <SectionCard icon={Target} title="Finalización" testId="player-analysis-finishing">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {visible.map((i) => (
          <Metric
            key={i.key}
            label={i.label}
            value={i.value}
            hint={i.hint}
            testId={`fin-${i.key}`}
          />
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Passing                                                                    */
/* -------------------------------------------------------------------------- */

function PassingCard({ passing, eventsCompleteness }) {
  if (!passing) return null;
  const items = [
    { key: 'attempted', label: 'Intentados', value: fmtNum(passing.attempted) },
    { key: 'completed', label: 'Completados', value: fmtNum(passing.completed) },
    { key: 'failed', label: 'Fallados', value: fmtNum(passing.failed) },
    { key: 'accuracy_pct', label: 'Precisión', value: fmtPct(passing.accuracy_pct) },
    { key: 'forward', label: 'Hacia delante', value: fmtNum(passing.forward) },
    { key: 'backward', label: 'Hacia atrás', value: fmtNum(passing.backward) },
    { key: 'lateral', label: 'Laterales', value: fmtNum(passing.lateral) },
    {
      key: 'avg_length_m', label: 'Long. media',
      value: passing.avg_length_m !== null ? `${fmtDec(passing.avg_length_m, 1)} m` : null,
    },
    {
      key: 'avg_completed_length_m', label: 'Long. media completados',
      value: passing.avg_completed_length_m !== null ? `${fmtDec(passing.avg_completed_length_m, 1)} m` : null,
    },
    {
      key: 'avg_failed_length_m', label: 'Long. media fallados',
      value: passing.avg_failed_length_m !== null ? `${fmtDec(passing.avg_failed_length_m, 1)} m` : null,
    },
    { key: 'key_passes', label: 'Key Pass', value: fmtNum(passing.key_passes) },
    { key: 'assists', label: 'Assists', value: fmtNum(passing.assists) },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  const eventsN = Array.isArray(passing.events) ? passing.events.length : 0;
  return (
    <SectionCard
      icon={Send}
      title="Pase"
      note={isPartial(eventsCompleteness) && eventsN > 0 ? 'Detalle parcial' : null}
      testId="player-analysis-passing"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {visible.map((i) => (
          <Metric key={i.key} label={i.label} value={i.value} testId={`pass-${i.key}`} />
        ))}
      </div>
      {isPartial(eventsCompleteness) && eventsN > 0 ? (
        <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
          Los agregados provienen del proveedor. El detalle por evento está disponible sólo para {eventsN} de {fmtNum(passing.attempted) ?? '—'} pases.
        </p>
      ) : null}
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Duels                                                                      */
/* -------------------------------------------------------------------------- */

function DuelsCard({ duels }) {
  if (!duels) return null;
  const items = [
    { key: 'total_events', label: 'Eventos totales', value: fmtNum(duels.total_events) },
    {
      key: 'resolved', label: 'Resueltos',
      value: fmtNum(duels.resolved),
      hint: duels.total_events !== null && duels.resolved !== null && duels.total_events !== duels.resolved
        ? `Excluye ${(duels.total_events - duels.resolved)} eventos no resueltos (p. ej., faltas concedidas).`
        : null,
    },
    { key: 'won', label: 'Ganados', value: fmtNum(duels.won) },
    { key: 'lost', label: 'Perdidos', value: fmtNum(duels.lost) },
    { key: 'fouls_conceded', label: 'Faltas concedidas', value: fmtNum(duels.fouls_conceded) },
    {
      key: 'win_pct', label: '% ganados',
      value: fmtPct(duels.win_pct),
      hint: 'Calculado sobre duelos resueltos (ganados + perdidos).',
    },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  return (
    <SectionCard icon={Swords} title="Duelos" testId="player-analysis-duels">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {visible.map((i) => (
          <Metric key={i.key} label={i.label} value={i.value} hint={i.hint} testId={`duel-${i.key}`} />
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Defending                                                                  */
/* -------------------------------------------------------------------------- */

function DefendingCard({ defending }) {
  if (!defending) return null;
  const items = [
    { key: 'recoveries', label: 'Recuperaciones', value: fmtNum(defending.recoveries) },
    { key: 'clearances', label: 'Clearances', value: fmtNum(defending.clearances) },
    { key: 'blocks', label: 'Blocks', value: fmtNum(defending.blocks) },
    { key: 'to_opponent', label: 'To opponent', value: fmtNum(defending.to_opponent) },
    { key: 'tackles_attempted', label: 'Tackles intentados', value: fmtNum(defending?.tackles?.attempted) },
    { key: 'tackles_won', label: 'Tackles ganados', value: fmtNum(defending?.tackles?.won) },
    { key: 'fouls_committed', label: 'Faltas cometidas', value: fmtNum(defending.fouls_committed) },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  return (
    <SectionCard icon={Shield} title="Defensa" testId="player-analysis-defending">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {visible.map((i) => (
          <Metric key={i.key} label={i.label} value={i.value} testId={`def-${i.key}`} />
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Crossing                                                                   */
/* -------------------------------------------------------------------------- */

function CrossingCard({ crossing }) {
  if (!crossing) return null;
  const items = [
    { key: 'attempted', label: 'Centros intentados', value: fmtNum(crossing.attempted) },
    { key: 'completed', label: 'Centros completados', value: fmtNum(crossing.completed) },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  return (
    <SectionCard icon={Target} title="Centros" testId="player-analysis-crossing">
      <div className="grid grid-cols-2 gap-3">
        {visible.map((i) => (
          <Metric key={i.key} label={i.label} value={i.value} testId={`cross-${i.key}`} />
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Team Impact                                                                */
/* -------------------------------------------------------------------------- */

function TeamImpactCard({ teamContext }) {
  if (!teamContext) return null;
  const share = teamContext.player_share_pct || {};
  const rows = [
    { key: 'shots', label: 'Tiros', pct: share.shots },
    { key: 'passes_attempted', label: 'Pases intentados', pct: share.passes_attempted },
    { key: 'passes_completed', label: 'Pases completados', pct: share.passes_completed },
    { key: 'crosses', label: 'Centros', pct: share.crosses },
    { key: 'tackles', label: 'Tackles', pct: share.tackles },
  ];
  const visible = rows.filter((r) => r.pct !== null && r.pct !== undefined);
  if (visible.length === 0) return null;
  return (
    <SectionCard icon={Users} title="Impacto en el equipo" testId="player-analysis-team-impact">
      <div className="flex flex-col gap-2">
        {visible.map((r) => (
          <div
            key={r.key}
            className="flex flex-col gap-1"
            data-testid={`impact-${r.key}`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-[var(--ns-text)] leading-tight">{r.label}</p>
              <p className="text-xs font-semibold text-[var(--ns-text)] tabular-nums shrink-0">
                {Number(r.pct).toFixed(1)}%
              </p>
            </div>
            <div className="h-1.5 rounded-full bg-[var(--ns-bg-2)] overflow-hidden">
              <div
                className="h-full bg-[var(--ns-gold-500)]"
                style={{ width: `${Math.max(0, Math.min(100, Number(r.pct)))}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Key Actions (regulation vs shootout separated)                             */
/* -------------------------------------------------------------------------- */

function KeyActionsCard({ actions }) {
  const list = Array.isArray(actions) ? actions : [];
  if (list.length === 0) return null;
  const regulation = list.filter((a) => a.event_label !== 'Penalty Shootout');
  const shootout = list.filter((a) => a.event_label === 'Penalty Shootout');
  return (
    <SectionCard icon={ListChecks} title="Acciones del partido" testId="player-analysis-key-actions">
      <div className="flex flex-col gap-4">
        <KeyActionsGroup title="Tiempo reglamentario" items={regulation} testId="key-actions-regulation" />
        {shootout.length > 0 ? (
          <KeyActionsGroup title="Tanda de penaltis" items={shootout} testId="key-actions-shootout" />
        ) : null}
      </div>
    </SectionCard>
  );
}

function KeyActionsGroup({ title, items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-[var(--ns-text-muted)] font-semibold mb-2">
        {title}
      </p>
      <div className="flex flex-col gap-1.5">
        {items.map((a, i) => {
          const ts = formatVideoTimestamp(a.timestamp);
          return (
            <div
              key={`${a.timestamp}-${i}`}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-xl border border-[var(--ns-border)] bg-[var(--ns-bg-2)]/40"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Badge
                  className={badgeClassForType(a.event_label, a.result)}
                >
                  {labelForType(a)}
                </Badge>
                {a.zone ? (
                  <span className="text-[11px] text-[var(--ns-text-muted)] truncate">{a.zone}</span>
                ) : null}
              </div>
              {ts ? (
                <span className="text-[10px] font-mono text-[var(--ns-text-subtle)]">
                  Vídeo {ts}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function labelForType(a) {
  const parts = [];
  if (a.event_label) parts.push(a.event_label);
  if (a.sub_event_label) parts.push(a.sub_event_label);
  return parts.join(' · ') || '—';
}

function badgeClassForType(label, result) {
  if (label === 'Shots' && result === 'Goal') {
    return 'bg-emerald-50 text-emerald-800 border border-emerald-200 uppercase tracking-wider text-[10px] rounded-full';
  }
  if (label === 'Penalty Shootout') {
    return 'bg-indigo-50 text-indigo-800 border border-indigo-200 uppercase tracking-wider text-[10px] rounded-full';
  }
  if (label === 'Shots') {
    return 'bg-amber-50 text-amber-900 border border-amber-200 uppercase tracking-wider text-[10px] rounded-full';
  }
  if (label === 'Cross') {
    return 'bg-sky-50 text-sky-800 border border-sky-200 uppercase tracking-wider text-[10px] rounded-full';
  }
  if (label === 'Passes') {
    return 'bg-slate-50 text-slate-800 border border-slate-200 uppercase tracking-wider text-[10px] rounded-full';
  }
  return 'bg-slate-50 text-slate-800 border border-slate-200 uppercase tracking-wider text-[10px] rounded-full';
}

/* -------------------------------------------------------------------------- */
/* Penalty Shootout                                                           */
/* -------------------------------------------------------------------------- */

function PenaltyShootoutCard({ shootout }) {
  if (!shootout) return null;
  const shots = fmtNum(shootout.shots);
  const goals = fmtNum(shootout.goals);
  if (shots === null || Number(shootout.shots) <= 0) return null;
  return (
    <SectionCard icon={Trophy} title="Tanda de penaltis" testId="player-analysis-shootout">
      <div className="grid grid-cols-2 gap-3">
        <Metric label="Lanzamientos" value={shots} testId="shootout-shots" />
        <Metric label="Goles" value={goals} testId="shootout-goals" />
      </div>
      <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
        No forma parte de las estadísticas del tiempo reglamentario.
      </p>
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Sequences                                                                  */
/* -------------------------------------------------------------------------- */

function SequencesCard({ sequences, status }) {
  if (!sequences) return null;
  const items = [
    { key: 'passes_given', label: 'Pases en secuencias', value: fmtNum(sequences.passes_given) },
    { key: 'passes_received', label: 'Recibidos en secuencias', value: fmtNum(sequences.passes_received) },
    { key: 'longest', label: 'Secuencia más larga', value: fmtNum(sequences.longest) },
    { key: 'team_total_sequences', label: 'Secuencias del equipo', value: fmtNum(sequences.team_total_sequences) },
  ];
  const visible = items.filter((i) => i.value !== null);
  if (visible.length === 0) return null;
  return (
    <SectionCard
      icon={GitBranch}
      title="Secuencias"
      note={isPartial(status) ? 'Parcial' : null}
      testId="player-analysis-sequences"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {visible.map((i) => (
          <Metric key={i.key} label={i.label} value={i.value} testId={`seq-${i.key}`} />
        ))}
      </div>
    </SectionCard>
  );
}
